// Composable for managing a single DomainTerm: fetches data, description, and auto-saves changes.
import { throttle } from "lodash";
import { storeToRefs } from "pinia";
import { Ref, computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { Artefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/markdown";
import { syncChannel } from "@/lib/broadcast-channels";
import { api, type types } from "@/lib/openapi";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { useReleaseStore } from "@/stores/release-store";

import { FILE_SYNC_THROTTLE_MS } from "./draft-manager";
import { useEditManager } from "./edit-manager";
import { useEntityNameManager } from "./entity-name-manager";
import { registerFlush } from "./pending-flush";

export type DomainTerm = types.DomainTerm;

export const useDomainTermStore = defineEntityStore("domainTerm", () => {
    const { organization } = useOrganizationManager();
    const { stek } = useStekManager();
    const domainTerm = ref<DomainTerm | undefined>(undefined);
    const router = useRouter();
    const description: Ref<Artefact<"text/markdown"> | undefined> = ref();
    const { domainTerm: domainTermInfo } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();
    const isFetching = ref(false);
    const isSaving = ref(false);
    const controller = ref(new AbortController());
    const {
        isModified: isNameModified,
        reset: resetName,
        persist: persistName,
    } = useEntityNameManager(domainTerm);

    async function updateDomainTerm(force = false) {
        const termId = domainTermInfo.value?.identity;
        if (!force && domainTerm.value && domainTerm.value.identity === termId)
            return;

        controller.value.abort();
        controller.value = new AbortController();

        if (!termId) {
            description.value = undefined;
            domainTerm.value = undefined;
            return;
        }

        isFetching.value = true;
        description.value = undefined;
        domainTerm.value = undefined;

        try {
            const { data, response } =
                await api.startDomainDomainTermMgtGetDomainTermStartPost({
                    body: { domain_term_id: termId },
                    signal: controller.value.signal,
                });
            if (!response.ok) {
                if (response.status === 404) {
                    router.replace({
                        name: "not-found",
                        params: {
                            orgName: organization.value?.name,
                            stekName: stek.value?.name,
                        },
                        query: {
                            type: "domain_term",
                            id: termId,
                        },
                    });
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            domainTerm.value = data as DomainTerm;
            await updateDescription(termId);
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") return;
            throw e;
        } finally {
            isFetching.value = false;
        }
    }

    watch(
        () => domainTermInfo.value?.identity,
        () => updateDomainTerm(),
        { immediate: true }
    );
    watch(
        () => releaseStore.activeRevision,
        () => {
            if (domainTerm.value) updateDomainTerm(true);
        }
    );

    const updateDescription = async (currentTermId?: string) => {
        if (!description.value && domainTerm.value) {
            try {
                const result = await getDescriptionArtefact(
                    domainTerm.value,
                    "domainTermDescription",
                    controller.value.signal
                );
                if (
                    !domainTerm.value ||
                    domainTerm.value.identity !== currentTermId
                )
                    return;
                description.value = result;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError")
                    return;
                throw e;
            }
        }
    };

    const isModified = computed(
        () =>
            !isFetching.value &&
            Boolean(isNameModified.value || description.value?.isModified)
    );

    const save = async () => {
        if (!isModified.value || !domainTerm.value || isSaving.value) return;

        const body: Record<string, unknown> = {
            domain_term_id: domainTerm.value.identity,
        };

        if (isNameModified.value) body.name = domainTerm.value.name;
        if (description.value?.isModified)
            body.description = description.value.blob;

        // Only update if there's something beyond the id to change.
        if (Object.keys(body).length === 1) return;

        isSaving.value = true;
        try {
            const { response } =
                await api.startDomainDomainTermMgtUpdateDomainTermStartPost({
                    body,
                });
            if (response.ok) {
                if (description.value?.isModified) description.value.persist();
                if (isNameModified.value) persistName();

                // Broadcast the sync so other components can react.
                syncChannel.postMessage({
                    message: "synced",
                    identity: {
                        organizationId: organization.value?.identity,
                        entityType: EntityType.DOMAIN_TERM,
                        entityId: domainTerm.value.identity,
                        attribute: "description",
                    },
                });
            }
        } finally {
            isSaving.value = false;
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
        resetName();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(save, FILE_SYNC_THROTTLE_MS);
    registerFlush(() => throttledSave.flush());
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) throttledSave();
    });

    syncChannel.addEventListener(
        "message",
        ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.organizationId === organization.value?.identity &&
                identity.entityType === EntityType.DOMAIN_TERM &&
                identity.entityId === domainTerm.value?.identity
            ) {
                updateDomainTerm(true);
            }
        }
    );

    return { domainTerm, description, isFetching, isModified, reset };
});

export const useDomainTermManager = () => storeToRefs(useDomainTermStore());
